import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabaseClient';
import Cropper from 'react-easy-crop';
import { getCroppedImg } from '../../utils/cropImage';
import { v4 as uuidv4 } from 'uuid';

const Profile = () => {
  const [profile, setProfile] = useState({
    full_name: '',
    email: '',
    phone_number: '',
    country: '',
    county: '',
    age_range: '',
    program: '',
    interest_areas: [],
    experience: '',
    avatar_url: ''
  });

  const [avatarPreview, setAvatarPreview] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const [avatarEditorVisible, setAvatarEditorVisible] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error || !user) return;

      const { data, error: profileError } = await supabase
        .from('registrations')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (profileError) return console.error(profileError);

      setProfile({
        ...data,
        full_name: user.user_metadata.full_name || '',
        email: user.email || ''
      });

      if (data.avatar_url) {
        const { data: publicURL } = supabase.storage.from('avatars').getPublicUrl(data.avatar_url);
        setAvatarPreview(publicURL.publicUrl);
      }
    };

    fetchProfile();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({ ...prev, [name]: value }));
  };

  const handleAvatarSelection = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result);
      setAvatarEditorVisible(true);
    };
    reader.readAsDataURL(file);
  };

  const onCropComplete = (_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  };

  const handleAvatarSave = async () => {
    try {
      const blob = await getCroppedImg(imageSrc, croppedAreaPixels);
      if (!blob) {
        console.error('❌ Cropped blob is null or undefined');
        alert('Image crop failed.');
        return;
      }

      const userId = (await supabase.auth.getUser()).data.user.id;
      const fileName = `${userId}/${uuidv4()}.jpeg`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, blob, {
          contentType: 'image/jpeg',
          upsert: true
        });

      if (uploadError) {
        console.error('❌ Upload Error:', uploadError);
        alert('Upload failed. See console.');
        return;
      }

      const { data: publicURL } = supabase.storage.from('avatars').getPublicUrl(fileName);
      setAvatarPreview(publicURL.publicUrl);
      setProfile(prev => ({ ...prev, avatar_url: fileName }));
      setAvatarEditorVisible(false);

      const { error: updateError } = await supabase
        .from('registrations')
        .update({ avatar_url: fileName })
        .eq('email', profile.email);

      if (updateError) {
        console.error('❌ Failed to update profile with avatar:', updateError);
      }

    } catch (err) {
      console.error('🚨 Unexpected error in handleAvatarSave:', err);
      alert('Unexpected error. See console.');
    }
  };

  const handleSave = async () => {
    const { error } = await supabase
      .from('registrations')
      .update({
        phone_number: profile.phone_number,
        country: profile.country,
        county: profile.county,
        age_range: profile.age_range,
        program: profile.program,
        interest_areas: profile.interest_areas,
        experience: profile.experience,
        avatar_url: profile.avatar_url
      })
      .eq('email', profile.email);

    if (error) return alert('Failed to update profile');
    alert('✅ Profile updated!');
    setIsEditing(false);
  };

  return (
    <div className="bg-white p-8 rounded-xl shadow max-w-6xl mx-auto">
      <h2 className="text-3xl font-bold text-indigo-700 mb-8">Your Profile</h2>
      <div className="grid md:grid-cols-3 gap-12 items-start">
        <div className="md:col-span-2 space-y-4">
          {[{ label: "Full Name", name: "full_name", readOnly: true }, { label: "Email", name: "email", readOnly: true }, { label: "Phone Number", name: "phone_number" }, { label: "Country", name: "country" }, { label: "County", name: "county" }].map(({ label, name, readOnly }) => (
            <label className="block" key={name}>
              <span className="text-gray-700">{label}</span>
              <input
                name={name}
                value={profile[name] || ''}
                onChange={handleChange}
                className={`w-full px-4 py-2 mt-1 border border-gray-300 rounded ${readOnly || !isEditing ? 'bg-gray-100' : ''}`}
                readOnly={readOnly || !isEditing}
              />
            </label>
          ))}

          <label className="block">
            <span className="text-gray-700">Age Range</span>
            <select
              name="age_range"
              value={profile.age_range}
              onChange={handleChange}
              disabled={!isEditing}
              className="w-full px-4 py-2 mt-1 border border-gray-300 rounded"
            >
              <option value="">Select</option>
              <option value="Under 18">Under 18</option>
              <option value="18-24">18–24</option>
              <option value="25-34">25–34</option>
              <option value="35+">35+</option>
            </select>
          </label>

          <label className="block">
            <span className="text-gray-700">Program</span>
            <select
              name="program"
              value={profile.program}
              onChange={handleChange}
              disabled={!isEditing}
              className="w-full px-4 py-2 mt-1 border border-gray-300 rounded"
            >
              <option value="">Select</option>
              <option value="EmpowerHer">EmpowerHer</option>
              <option value="Girls-To-Tech">Girls-To-Tech</option>
            </select>
          </label>

          <label className="block">
            <span className="text-gray-700">Experience Level</span>
            <select
              name="experience"
              value={profile.experience}
              onChange={handleChange}
              disabled={!isEditing}
              className="w-full px-4 py-2 mt-1 border border-gray-300 rounded"
            >
              <option value="Beginner">Beginner</option>
              <option value="Intermediate">Intermediate</option>
              <option value="Advanced">Advanced</option>
            </select>
          </label>

          <div className="flex gap-4 mt-6">
            {!isEditing ? (
              <button onClick={() => setIsEditing(true)} className="bg-indigo-600 text-white px-6 py-2 rounded hover:bg-indigo-700">
                Edit Profile
              </button>
            ) : (
              <>
                <button onClick={handleSave} className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700">
                  Save Changes
                </button>
                <button onClick={() => setIsEditing(false)} className="bg-gray-300 text-gray-800 px-6 py-2 rounded hover:bg-gray-400">
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>

        <div className="text-center">
          <img
            src={avatarPreview || '/images/default-avatar.png'}
            alt="Profile Avatar"
            className="w-32 h-32 rounded-full mx-auto border object-cover"
          />
          {isEditing && !avatarEditorVisible && (
            <div className="mt-4">
              <label className="block text-gray-700 font-medium mb-1">Change Avatar</label>
              <input type="file" accept="image/*" onChange={handleAvatarSelection} />
            </div>
          )}

          {avatarEditorVisible && (
            <div className="mt-4 space-y-2">
              <div className="relative w-full h-[300px] bg-gray-100 rounded">
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              </div>
              <input
                type="range"
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(e.target.value)}
                className="w-full"
              />
              <div className="flex justify-center gap-4">
                <button onClick={handleAvatarSave} className="bg-green-600 text-white px-4 py-2 rounded">
                  Save Avatar
                </button>
                <button onClick={() => setAvatarEditorVisible(false)} className="bg-gray-300 px-4 py-2 rounded">
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
